const steps = [
  {
    number: '01',
    title: 'Site Evaluation & Testing',
    desc: 'Comprehensive on-site inspection and environmental sampling to identify hazards, contamination scope, and regulatory requirements.',
    icon: 'ri-search-eye-line',
  },
  {
    number: '02',
    title: 'Risk Assessment & Compliance Planning',
    desc: 'Detailed regulatory analysis and a project-specific compliance plan aligned with EPA, OSHA, and state requirements.',
    icon: 'ri-file-list-3-line',
  },
  {
    number: '03',
    title: 'Containment & Safety Setup',
    desc: 'Full containment systems, negative air pressure, HEPA filtration, and PPE protocols established before any removal begins.',
    icon: 'ri-shield-line',
  },
  {
    number: '04',
    title: 'Certified Removal & Remediation',
    desc: 'Licensed specialists execute safe, efficient removal and remediation per all applicable regulatory standards and best practices.',
    icon: 'ri-tools-line',
  },
  {
    number: '05',
    title: 'Clearance Testing & Documentation',
    desc: 'Post-remediation air and surface testing with complete documentation, clearance certificates, and full regulatory reporting.',
    icon: 'ri-checkbox-circle-line',
  },
];

interface ProcessSectionProps {
  title?: string;
}

export default function ProcessSection({ title = 'A Proven Process for Safe, Compliant Results' }: ProcessSectionProps) {
  return (
    <section id="process" className="bg-ecos-charcoal-mid py-28 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start mb-16">
          <div className="lg:col-span-5">
            <p className="text-ecos-green text-xs uppercase tracking-widest font-semibold mb-4">How We Work</p>
            <h2 className="font-serif text-white text-4xl lg:text-5xl font-semibold leading-tight">
              {title}
            </h2>
          </div>
          <div className="lg:col-span-7 flex items-end">
            <p className="text-white/45 text-base leading-relaxed">
              Every project follows our proven 5-step framework — built on decades of compliance experience, refined on thousands of commercial, industrial, and government projects across Colorado.
            </p>
          </div>
        </div>

        <div className="relative">
          {/* Connector line */}
          <div className="hidden lg:block absolute top-10 left-10 right-10 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent"></div>

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-8">
            {steps.map((step, idx) => (
              <div key={step.number} className="relative flex flex-col">
                <div className="relative z-10 flex flex-col items-start mb-6">
                  <div className="w-20 h-20 flex items-center justify-center bg-ecos-charcoal-surface border border-white/12 rounded-sm mb-0 relative">
                    <div className="w-10 h-10 flex items-center justify-center">
                      <i className={`${step.icon} text-ecos-green text-2xl`}></i>
                    </div>
                    <span className="absolute -top-2 -right-2 bg-ecos-green text-white font-mono text-xs font-semibold w-6 h-6 flex items-center justify-center rounded-sm">
                      {idx + 1}
                    </span>
                  </div>
                </div>
                <h3 className="font-serif text-white text-lg font-semibold mb-3 leading-snug">{step.title}</h3>
                <p className="text-white/45 text-sm leading-relaxed flex-1">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-16 pt-12 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-6">
          <p className="text-white/55 text-sm max-w-md text-center sm:text-left">
            Ready to begin? Our certified specialists are available for emergency response and scheduled assessments.
          </p>
          <div className="flex items-center gap-4 flex-shrink-0">
            <a
              href="tel:+18888685420"
              className="flex items-center gap-2 border border-white/20 hover:border-ecos-green text-white hover:text-ecos-green px-5 py-2.5 rounded-sm text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-line text-sm"></i>
              Call 888-868-5420
            </a>
            <a
              href="/#assessment"
              className="bg-ecos-green hover:bg-ecos-green-light text-white px-6 py-2.5 rounded-sm text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
            >
              Start Assessment
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
